/* eslint-disable */
import { Button, Input } from '@enterprise-ui/canvas-ui-react'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import Messages from './components/Messages'
import { Nav } from './components/Nav'
import Disclaimer from './components/Disclaimer'
import apiConfig from './apiConfig'
import { handleApiError } from './utils/errorHandler'
import { v4 as uuidv4 } from 'uuid'
import { CircleArrowUp } from 'lucide-react'
import { trackInteraction } from './utils/trackEvent'
import { themeColors } from './theme'
import './Welcome.scss'

interface Message {
  role: string
  content: string
  question: string
  timeTaken: number
  isComplete?: boolean
  conversationId?: string
  timestamp?: number
  inappropriate_flag?: boolean
}

const initialMessage: Message[] = [
  {
    role: 'assistant',
    content:
      "Hi! I'm Target's AI Career Assistant, here to help answer questions you might have related to jobs at Target. To get started, please ask a question",
    timeTaken: 0,
    isComplete: true,
    question: '',
    inappropriate_flag: false,
  },
]

interface WelcomeProps {
  onClose: () => void
  onMinimize: () => void
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  chatEntryRef?: React.RefObject<HTMLDivElement | null>
}

const Welcome: React.FC<WelcomeProps> = ({
  onClose,
  onMinimize,
  messages,
  setMessages,
}) => {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const updateBodyClass = () => {
      if (mediaQuery.matches) {
        document.body.classList.remove('theme-light')
        document.body.classList.add('theme-dark')
      } else {
        document.body.classList.remove('theme-dark')
        document.body.classList.add('theme-light')
      }
    }
    updateBodyClass()
    mediaQuery.addEventListener('change', updateBodyClass)
    return () => {
      mediaQuery.removeEventListener('change', updateBodyClass)
    }
  }, [])
  const chatEntryRef = useRef<HTMLDivElement>(null)

  const [inputText, setInputText] = useState('')
  const [loading, setLoading] = useState(false)
  const [fontSize, setFontSize] = useState(14)
  const [conversationId, setConversationId] = useState<string>(() => uuidv4())
  const default_suggestedQuestions = ["What's it like to work at Target?"]
  const MESSAGE_LIMIT = 8

  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(
    default_suggestedQuestions,
  )
  const [suggestedShown, setSuggestedShown] = useState<boolean>(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const [isHealthy, setIsHealthy] = useState<boolean>(false)
  const [healthError, setHealthError] = useState<boolean>(false)
  const getUserMessageCount = () =>
    messages.filter((msg) => msg.role === 'user').length

  const hasReachedMessageLimit = getUserMessageCount() >= MESSAGE_LIMIT

  useEffect(() => {
    if (!conversationId) {
      const newConversationId = uuidv4()
      setConversationId(newConversationId)
      setMessages([
        {
          role: 'assistant',
          content:
            "Hi! I'm Target's AI Career Assistant, here to help answer questions you might have related to jobs at Target. To get started, please ask a question",
          timeTaken: 0,
          isComplete: true,
          question: '',
          conversationId: conversationId,
        },
      ])
    }
  }, [conversationId, setMessages])

  const disclaimerText = (
    <span style={{ color: themeColors.standardText }}>
      By using this chat feature, you agree to{' '}
      <a
        href="https://www.target.com/c/target-privacy-policy/-/N-4sr7p"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: themeColors.link,
          textDecoration: 'underline',
          fontWeight: 'bold',
        }}
        onClick={() =>
          trackInteraction("Target's Privacy Policy", 'disclaimer_link')
        }
      >
        Target's Privacy Policy
      </a>{' '}
      and{' '}
      <a
        href="https://www.target.com/c/terms-conditions/-/N-4sr7l"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: themeColors.link,
          textDecoration: 'underline',
          fontWeight: 'bold',
        }}
        onClick={() =>
          trackInteraction('Terms & Conditions', 'disclaimer_link')
        }
      >
        Terms & Conditions
      </a>
      , and consent to the recording and sharing of this chat with Target and
      its service providers for operational purposes. The chat's automated
      assistant is powered by artificial intelligence. Never share sensitive
      information like personal identification information or social security
      numbers.
    </span>
  )

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  const handleSend = useCallback(async () => {
    if (hasReachedMessageLimit || inputText.trim() === '') return
    trackInteraction('Send message', 'send_button')

    setSuggestedShown(true)
    setMessages((prevMessages: Message[]) => [
      ...prevMessages,
      {
        role: 'user',
        content: inputText,
        timeTaken: 0,
        isComplete: true,
        question: '',
        conversationId: conversationId,
      },
    ])

    const payload = {
      conversation_id: conversationId,
      messages: [
        ...messages,
        {
          role: 'user',
          content: inputText,
          conversationId: conversationId,
        },
      ],
    }

    setInputText('')
    setLoading(true)
    const start_time = Date.now()

    const MAX_RETRIES = 3
    let attempt = 0
    let success = false

    while (attempt < MAX_RETRIES && !success) {
      try {
        const response = await fetch(
          `${apiConfig?.chatApi?.host}${apiConfig?.chatApi?.chatEndpoint}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
            mode: 'cors',
            credentials: 'include',
          },
        )

        if (!response.ok) {
          throw new Error(`Chat request failed with status: ${response.status}`)
        }

        if (!response.body) {
          throw new Error('Response body is null')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')

        const assistantMessage: Message = {
          role: 'assistant',
          content: '',
          timeTaken: 0,
          isComplete: false,
          question: '',
          conversationId: conversationId,
        }

        setMessages((prevMessages: Message[]) => [
          ...prevMessages,
          assistantMessage,
        ])

        await reader.read().then(function processText({ done, value }): any {
          if (done) {
            const end_time = Date.now()
            assistantMessage.isComplete = true
            assistantMessage.timeTaken = end_time - start_time
            assistantMessage.timestamp = end_time
            setLoading(false)
            setMessages((prevMessages: Message[]) => {
              const lastMessage = prevMessages[prevMessages.length - 1]
              if (lastMessage.role === 'assistant') {
                return [...prevMessages.slice(0, -1), assistantMessage]
              } else {
                return [...prevMessages, assistantMessage]
              }
            })
            setSuggestedShown(false)
            setSuggestedQuestions([assistantMessage.question])
            return
          }

          const chunk = decoder.decode(value, { stream: true })
          const jsonObjects = chunk.match(/{[^{}]*}/g) || []
          const lastJsonObjectChunk = jsonObjects[jsonObjects.length - 1]
          try {
            const jsonObject = JSON.parse(lastJsonObjectChunk)
            if (jsonObject.answer) {
              assistantMessage.content = jsonObject.answer
            }
            if (jsonObject.followup_question) {
              assistantMessage.question = jsonObject.followup_question
            }
            if (jsonObject.inappropriate_flag) {
              assistantMessage.inappropriate_flag =
                jsonObject.inappropriate_flag
            }
          } catch (error) {
            console.log('Incomplete or invalid chunk:', chunk)
          }

          setMessages((prevMessages: Message[]) => {
            const lastMessage = prevMessages[prevMessages.length - 1]
            if (lastMessage.role === 'assistant') {
              lastMessage.content = assistantMessage.content
              return [...prevMessages.slice(0, -1), lastMessage]
            } else {
              return [...prevMessages, { ...assistantMessage }]
            }
          })

          return reader.read().then(processText)
        })

        success = true
      } catch (error) {
        attempt++
        console.error(`Attempt ${attempt} failed:`, error)

        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
        } else {
          const errorResponse = handleApiError(error, 'chat')
          console.error('Final attempt failed:', error)
          setLoading(false)
          const errorMessage: Message = {
            role: 'assistant',
            content: `${errorResponse.message}. ${errorResponse.details}`,
            timeTaken: 0,
            isComplete: true,
            question: '',
            conversationId: conversationId,
          }

          setMessages((prevMessages: Message[]) => [
            ...prevMessages,
            errorMessage,
          ])
        }
      }
    }
  }, [inputText, messages, setMessages, hasReachedMessageLimit])

  useEffect(() => {
    handleSend()
  }, [suggestedShown])

  const handleClickSuggestions = (event: React.MouseEvent) => {
    setSuggestedShown(true)
    const target = event.target as HTMLElement
    trackInteraction(target.textContent || '', 'suggested_question')
    setInputText(target.textContent ?? '')
  }

  useEffect(() => {
    const handleEnterPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleSend()
      }
    }

    const inputElement = inputRef.current
    if (inputElement) {
      inputElement.addEventListener('keydown', handleEnterPress)
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('keydown', handleEnterPress)
      }
    }
  }, [handleSend])

  const messagesContainerStyle: React.CSSProperties = {
    flexGrow: 1,
    overflowY: 'auto',
    padding: '1rem',
    maxHeight: '80vh',
    background: themeColors.background,
  }

  const inputContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0.5rem',
    borderTop: '1px solid #d1d5db',
    background: themeColors.background,
    position: 'sticky',
    bottom: 0,
    zIndex: 1000,
  }

  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiConfig?.chatApi?.host}${apiConfig?.chatApi?.healthEndpoint}`,
        {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
          mode: 'cors',
        },
      )
      if (!response.ok) {
        throw new Error('Health check failed')
      }
      setIsHealthy(true)
      setHealthError(false)
    } catch (error) {
      const errorResponse = handleApiError(error, 'health')
      console.error('Health check failed:', error)
      setIsHealthy(false)
      setHealthError(true)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '2rem',
            textAlign: 'center',
            color: themeColors.standardText,
          }}
        >
          <div
            style={{
              // marginBottom: '1rem',
              fontSize: '1.2rem',
              fontWeight: 'bold',
            }}
          >
            {errorResponse.message}
          </div>
          <div style={{ fontSize: '1rem', color: themeColors.standardText }}>
            {errorResponse.details}
          </div>
        </div>
      )
    }
  }, [])

  useEffect(() => {
    checkHealth()
  }, [checkHealth])

  useEffect(() => {
    if (messages.length === 0) {
      setMessages(initialMessage)
    }
  }, [])

  const handleSuggestedQuestion = (question: string | undefined) => {
    if (question) {
      trackInteraction(question, 'suggested_question')
      setInputText(question)
      setSuggestedShown(true)
    }
  }

  const hasUserMessages = () => {
    return messages.some((msg) => msg.role === 'user')
  }

  if (healthError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '2rem',
          textAlign: 'center',
          background: themeColors.background,
        }}
      >
        <div
          style={{
            // marginBottom: '1rem',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: themeColors.standardText,
          }}
        >
          Oops! It looks like something didn’t go as planned.
        </div>
        <div style={{ fontSize: '1rem', color: themeColors.standardText }}>
          While we work on it, feel free to explore more roles and resources on{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: themeColors.link,
              textDecoration: 'underline',
              fontWeight: '500',
            }}
            href="https://target.com/careers"
          >
            our career site
          </a>
        </div>
      </div>
    )
  }

  if (!isHealthy) {
    return (
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          visibility: loading ? 'visible' : 'hidden',
          color: themeColors.standardText,
        }}
      >
        Writing Message...
      </div>
    )
  }
  return (
    <div className="chatbot-window">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          backgroundColor: themeColors.background,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        }}
      >
        <div style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
          <Nav
            fontSize={fontSize}
            setFontSize={setFontSize}
            onClose={() => {
              trackInteraction('Close chatbot', 'close_button')
              onClose()
            }}
            onMinimize={() => {
              trackInteraction('Minimize chatbot', 'minimize_button')
              onMinimize()
            }}
          />
        </div>
        <Disclaimer
          val={disclaimerText}
          firstMessage={
            messages.length === 3 && messages[2].role === 'assistant'
          }
        />
        <div
          style={{
            ...messagesContainerStyle,
            paddingBottom: '5rem',
            borderLeft: `1px solid ${themeColors.border}`,
            borderRight: `1px solid ${themeColors.border}`,
            borderTop: 'none',
            borderBottom: 'none',
            background: themeColors.background,
          }}
        >
          <Messages
            messages={messages}
            fontSize={fontSize}
            loading={loading}
            onSuggestedQuestion={handleSuggestedQuestion}
          />
          <div
            id="chat-responses"
            aria-live="polite"
            tabIndex={-1}
            ref={messagesEndRef}
          />
        </div>
        {hasReachedMessageLimit ? (
          <div
            style={{
              padding: '16px',
              textAlign: 'center',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              margin: '16px',
              fontSize: '14px',
            }}
          >
            You have reached the maximum number of messages. Please close the
            chatbot and open a new one to start a fresh conversation.
          </div>
        ) : (
          <>
            {!suggestedShown &&
              !hasUserMessages() &&
              suggestedQuestions?.length > 0 && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderLeft: `1px solid ${themeColors.border}`,
                    borderRight: `1px solid ${themeColors.border}`,
                    borderBottom: `1px solid ${themeColors.border}`,
                    backgroundColor: themeColors.background,
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      color: themeColors.standardText,
                      marginBottom: '8px',
                    }}
                  >
                    You might want to ask
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    {suggestedQuestions.map((question) => (
                      <button
                        key={question}
                        onClick={handleClickSuggestions}
                        style={{
                          background: themeColors.background,
                          border: `1px solid ${themeColors.suggestedChatText}`,
                          borderRadius: '20px',
                          padding: '8px 16px',
                          color: themeColors.suggestedChatText,
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            themeColors.suggestedChatText
                          e.currentTarget.style.color = themeColors.background
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            themeColors.background
                          e.currentTarget.style.color =
                            themeColors.suggestedChatText
                        }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            <div
              style={{
                ...inputContainerStyle,
                marginBottom: 'env(safe-area-inset-bottom)',
                borderTop: `1px solid ${themeColors.border}`,
                borderLeft: `1px solid ${themeColors.border}`,
                borderRight: `1px solid ${themeColors.border}`,
                background: themeColors.background,
              }}
            >
              <Input.Textarea
                ref={inputRef}
                id="message"
                name="message"
                value={inputText}
                disabled={loading}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setInputText(e.target.value)
                }
                placeholder="Try a question"
                rows={1}
                style={{
                  flexGrow: 1,
                  padding: '0.5rem',
                  fontSize: '16px',
                  lineHeight: '1.4',
                  WebkitTextSizeAdjust: '100%',
                  border: '1px solid black',
                  borderRadius: '0.25rem',
                  marginRight: '0.5rem',
                  backgroundColor: themeColors.background,
                  height: 'auto',
                  color: themeColors.standardText,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  fontFamily: 'HelveticaForTarget, Arial, sans-serif',
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (inputText.trim() !== '') {
                    trackInteraction('Send message', 'send_button')
                  }
                  handleSend()
                }}
                aria-label="Send Message"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <CircleArrowUp
                  size={24}
                  color={themeColors.arrowUpChatSubmit}
                />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Welcome
